
import React, { useState, useEffect } from 'react';
import hanjaData from '../data/hanjaData.json';
import { Button, TextField, Box, Typography, Grid } from '@mui/material';

interface QuizPageProps {
  hanjaDisplaySize: number;
}

const QuizPage: React.FC<QuizPageProps> = ({ hanjaDisplaySize }) => {
  const [quizType, setQuizType] = useState<'multiple-choice' | 'short-answer' | null>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showScore, setShowScore] = useState(false);

  const startQuiz = (type: 'multiple-choice' | 'short-answer') => {
    const shuffledData = [...hanjaData].sort(() => 0.5 - Math.random());
    const selectedQuestions = shuffledData.slice(0, 10);

    if (type === 'multiple-choice') {
      const questionsWithChoices = selectedQuestions.map(q => {
        const wrongAnswers = hanjaData
          .filter(h => h.id !== q.id)
          .sort(() => 0.5 - Math.random())
          .slice(0, 3)
          .map(h => `${h.sound}-${h.meaning}`);
        const choices = [...wrongAnswers, `${q.sound}-${q.meaning}`].sort(() => 0.5 - Math.random());
        return { ...q, choices };
      });
      setQuestions(questionsWithChoices);
    } else {
      setQuestions(selectedQuestions);
    }

    setQuizType(type);
    setCurrentQuestionIndex(0);
    setScore(0);
    setShowScore(false);
    setUserAnswer('');
  };

  const handleAnswer = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const correctAnswerWithHyphen = `${currentQuestion.sound}-${currentQuestion.meaning}`;
    const correctAnswerWithoutHyphen = `${currentQuestion.sound}${currentQuestion.meaning}`;

    if (quizType === 'short-answer') {
      const userAnswerTrimmed = userAnswer.trim();
      if (userAnswerTrimmed === correctAnswerWithHyphen.trim() || 
          userAnswerTrimmed === correctAnswerWithoutHyphen.trim() ||
          userAnswerTrimmed === `${currentQuestion.meaning}-${currentQuestion.sound}`.trim() ||
          userAnswerTrimmed === `${currentQuestion.meaning}${currentQuestion.sound}`.trim()
      ) {
        setScore(prev => prev + 1);
      }
    } 

    const nextQuestionIndex = currentQuestionIndex + 1;
    if (nextQuestionIndex < questions.length) {
      setCurrentQuestionIndex(nextQuestionIndex);
      setUserAnswer('');
    } else {
      setShowScore(true);
    }
  };

  const handleChoiceClick = (choice: string) => {
    const currentQuestion = questions[currentQuestionIndex];
    const correctAnswer = `${currentQuestion.sound}-${currentQuestion.meaning}`;
    if (choice === correctAnswer) {
      setScore(prev => prev + 1);
    }
    const nextQuestionIndex = currentQuestionIndex + 1;
    if (nextQuestionIndex < questions.length) {
      setCurrentQuestionIndex(nextQuestionIndex);
    } else {
      setShowScore(true);
    }
  }

  if (showScore) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>퀴즈 결과</Typography>
        <Typography variant="h5">총 10문제 중 {score}문제를 맞혔습니다.</Typography>
        <Button variant="contained" onClick={() => { setQuizType(null); setShowScore(false); }} sx={{ mt: 2 }}>
          다시하기
        </Button>
      </Box>
    );
  }

  if (!quizType) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>퀴즈 유형 선택</Typography>
        <Button variant="contained" onClick={() => startQuiz('multiple-choice')} sx={{ m: 1 }}>
          객관식
        </Button>
        <Button variant="contained" onClick={() => startQuiz('short-answer')} sx={{ m: 1 }}>
          주관식
        </Button>
      </Box>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>한자 퀴즈 ({currentQuestionIndex + 1}/10)</Typography>
      <Typography variant="h1" sx={{ my: 2, fontSize: hanjaDisplaySize * 0.8 }}>{currentQuestion.hanja}</Typography>
      
      {quizType === 'multiple-choice' && (
        <Grid container spacing={2} justifyContent="center" maxWidth="sm">
          {currentQuestion.choices.map((choice: string, index: number) => (
            <Grid key={index} component="div" sx={{ width: '50%' }}>
              <Box sx={{ width: '100%' }}>
                <Button variant="contained" fullWidth onClick={() => handleChoiceClick(choice)}>
                  {choice.split('-').reverse().join('-')} {/* Display as 음-뜻 */}
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {quizType === 'short-answer' && (
        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleAnswer(); }} sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <TextField 
            variant="outlined"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            label="음-뜻 (예: 일-날 또는 일날)"
          />
          <Button type="submit" variant="contained" sx={{ ml: 1, height: '56px' }}>
            제출
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default QuizPage;
